/* eslint-disable no-console */
import { ApiError } from '@cbhq/data-layer';
import {
  CbDataRequestInit,
  NonThrowingFetch,
  ReReadableOkResponse,
} from '@cbhq/data-layer/network';

import { isRegressionTest } from '../utils';

export type NonThrowingFetchWithMocks = (
  url: string,
  init?: CbDataRequestInit,
) => ReturnType<NonThrowingFetch>;

/** Type for the package-specific URL → mock response resolver passed to createMockFetch. */
export type MockResponseGetter = (url: string, init?: CbDataRequestInit) => unknown;

async function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

// Header names whose values must not appear in logs (auth tokens, session cookies, etc.)
const SENSITIVE_HEADER_PATTERN = /authorization|cookie|token|secret|session/i;

function sanitizeHeaders(
  headers: Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (!headers) return headers;
  return Object.fromEntries(
    Object.entries(headers).map(([key, value]) => [
      key,
      SENSITIVE_HEADER_PATTERN.test(key) ? '[REDACTED]' : value,
    ]),
  );
}

function logRequestInfo(url: string, init: CbDataRequestInit | undefined, response: unknown): void {
  if (process.env.NODE_ENV !== 'test') {
    const sanitizedInit = init
      ? { ...init, headers: sanitizeHeaders(init.headers as Record<string, string> | undefined) }
      : init;
    // Log response shape only — values may contain realistic mock PII
    const responseShape =
      response !== null && typeof response === 'object'
        ? Object.keys(response as object)
        : typeof response;
    console.groupCollapsed(url);
    console.log('req:', sanitizedInit);
    console.log('res shape:', responseShape);
    console.groupEnd();
  }
}

type CreateMockFetchOptions = {
  clientId?: string;
  projectId?: string;
};

/**
 * Wraps a package-specific getMockResponse function into a NonThrowingFetch
 * compatible function. Handles logging, ApiError passthrough, and the artificial
 * delay used to make loading states visible during development.
 *
 * Each package still owns its own useMockHandler (URL routing / mock data).
 * This utility centralises the boilerplate response construction.
 *
 * Pass either `clientId` or `projectId` — if either equals `'percy'` the delay
 * is skipped so Percy snapshots always capture the fully-loaded state.
 *
 * @param getMockResponse  Package-provided URL → mock response resolver.
 * @param clientId         Consumer client ID (e.g. from ClientPropsProvider).
 * @param projectId        Consumer project ID (e.g. from NavbarProps).
 */
export function createMockFetch(
  getMockResponse: MockResponseGetter,
  { clientId, projectId }: CreateMockFetchOptions = {},
): NonThrowingFetchWithMocks {
  const isTest = process.env.NODE_ENV === 'test';
  const delay = isTest || isRegressionTest({ clientId, projectId }) ? 0 : 1000;
  return async (url: string, init?: CbDataRequestInit) => {
    const response = getMockResponse(url, init);

    logRequestInfo(url, init, response);

    if (response instanceof ApiError) {
      return response;
    }

    if (delay > 0) {
      await wait(delay);
    }

    return {
      requestTime: 0,
      timeUntilFirstByte: 0,
      timeUntilFirstChunk: 0,
      headers: init?.headers,
      status: 200,
      json: async () => response,
      text: async () => JSON.stringify(response),
      iterable: () => {
        throw new Error('Not implemented');
      },
      ok: true,
      body: init?.body,
      url,
    } as ReReadableOkResponse;
  };
}
